import type { Customer, FieldDef } from './types'

export interface ScheduleService { id: number; name: string; serviceConfigType: number; type?: number }
export interface ScheduleDay { date: string; hasSlotLeft: boolean }
export interface ScheduleSession {
  id: number; start: string; end: string; capacity: number; utilization: number
  hasSlotsLeft: boolean; resourceId?: number | null; resourceIds?: number[]
  serviceConfigType: number; attendenceServiceType: number
}
export interface ScheduleAvailability {
  resources: { id: number; name: string }[]
  sessions: ScheduleSession[]
}
export interface ScheduleTime { start: string; end: string }
export interface SchedulePayment { hasPayment: boolean; realPrice: number }
export interface BookingInput {
  serviceId: number; sessionId: number; resourceId?: number; bookedStartTime?: string
  doFit: boolean; attendanceType: number; priority: number; customer: Customer
  customData: { customFieldId: number; value: string }[]; payment: SchedulePayment
}
export interface BookingReceipt { aggregatorId: string }
export interface BookingStatus { pending: number; error: number; success: number }

// Scheduling returns UTC instants; unqualified legacy times use the unit's UTC-3.
export const scheduleInstant = (value: string): number =>
  Date.parse(/[Zz]|[+-]\d{2}:\d{2}$/.test(value) ? value : value + '-03:00')
export const scheduleTime = (value: string): string =>
  new Date(scheduleInstant(value)).toLocaleTimeString('pt-BR', { timeZone: 'America/Maceio', hour: '2-digit', minute: '2-digit' })
export const scheduleDate = (value: Date): string => {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Maceio', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(value)
  return ['year', 'month', 'day'].map((key) => parts.find((p) => p.type === key)?.value).join('-')
}
export const scheduleToday = (): string => scheduleDate(new Date())

// Count CSV records, including quoted fields with embedded newlines.
export function scheduleCsvCount(text: string): number {
  let quoted = false
  let records = 0
  let content = false
  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    if (char === '"') {
      if (quoted && text[i + 1] === '"') {i++}
      else {quoted = !quoted}
    }
    if (char === '\n' && !quoted) {if (content) {records++}; content = false}
    else if (char.trim() && char !== '\uFEFF') {content = true}
  }
  if (quoted) {throw new Error('A planilha contém um campo com aspas incompletas.')}
  if (content) {records++}
  return Math.max(0, records - 1)
}
export const canBookSession = (session: ScheduleSession, now = Date.now()): boolean =>
  scheduleInstant(session.end) > now && (session.serviceConfigType !== 0 || session.hasSlotsLeft)
export const sessionMatchesResource = (session: ScheduleSession, resourceId: number): boolean =>
  resourceId === 0 || session.resourceId === resourceId || !!session.resourceIds?.includes(resourceId)
export const scheduleCustomData = (fields: FieldDef[], values: Record<string, string>): BookingInput['customData'] =>
  fields.filter((field) => values[field.key]?.trim()).map((field) => ({ customFieldId: Number(field.key.replace('customField:', '')), value: values[field.key] }))
export const scheduleCsv = (fields: FieldDef[]): string => {
  const quote = (value: string) => '"' + value.replace(/"/g, '""') + '"'
  return '\uFEFF' + ['Nome', 'E-mail', 'Telefone', 'Prioridade', ...fields.map((f) => `CF_${f.key.replace('customField:', '')}_${f.label}${f.required ? '_Requerido' : ''}`)].map(quote).join(',') + '\n'
}
